// Pure morning-brief formatters shared by the Overview panel and the
// Automations card (no React, no storage).

import { calendarWeekday } from '../../shared/dates';
import type { BriefLink } from '../../shared/morningBrief';
import { formatDayMonth } from './calendar';

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MODEL_LABELS: Record<string, string> = {
  'claude-opus-5-5': 'Claude Opus 5.5',
  'claude-sonnet-5-5': 'Claude Sonnet 5.5',
};

/** "Claude Opus 5.5" for known routine models, the raw id otherwise. */
export function briefModelLabel(model: string | null | undefined): string {
  if (!model) return 'unknown model';
  return MODEL_LABELS[model] ?? model;
}

/** "05:01" — 24-hour Melbourne clock, as the mockups stamp it. Empty for
    a missing or non-numeric timestamp, so a partial row never throws. */
export function briefClock(ms: number | null | undefined): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return '';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Melbourne', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(new Date(ms));
}

/** "Mon 12 Oct" from a Melbourne business date. */
export function briefDayLabel(iso: string): string {
  return `${WEEKDAY_SHORT[calendarWeekday(iso)] ?? ''} ${formatDayMonth(iso)}`.trim();
}

export function briefWeekdayName(iso: string): string {
  return WEEKDAY_LONG[calendarWeekday(iso)] ?? 'the last';
}

/** Where an entity link goes. Holds have no route, so they stay plain text;
    Tasks has no focus/highlight param, so task links open the list. */
export function briefLinkHref(link: BriefLink | undefined): string | null {
  if (!link || !link.id) return null;
  switch (link.kind) {
    case 'project': return `/projects/${link.id}`;
    case 'lead': return `/leads/${link.id}`;
    case 'invoice': return `/finance/invoices/${link.id}`;
    case 'client': return `/clients/${link.id}`;
    case 'task': return '/tasks';
    default: return null;
  }
}

/** "1 item" / "14 items". */
export function briefItemCount(n: number | null | undefined): string {
  const count = typeof n === 'number' && Number.isFinite(n) ? n : 0;
  return `${count} item${count === 1 ? '' : 's'}`;
}
